import { Card } from "@rothoblaas/design-system";
import { type FC, memo, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useI18n from "../../../hooks/useTranslations";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { getSideCode } from "./DeckEdgeMeasurements";
import styles from "./DeckSlopeViewCard.module.css";
import { useMeasurementFormat } from "./measurementFormat";
import {
  DEGREES_TO_RADIANS,
  getCoordinateBounds,
  getLShapeDimensions,
  getSegmentLength,
  METERS_TO_MILLIMETERS,
} from "./deckGeometry";
import { getFootprintCoordinates, getTerrainRiseMmAt } from "./deckOutline";
import {
  DIMENSION_COLOR,
  GROUND_FILL_COLOR,
  GROUND_HATCH_COLOR,
  GROUND_HATCH_ID,
  LABEL_COLOR,
  TOTAL_HEIGHT_COLOR,
} from "./deckSlopeViewConstants";
import SlopeSection from "./SlopeSection";
import { getPreviewSize, type SectionPreviewSize } from "./ZoomableSectionPreview";
import { getNumberFormat } from "../../../utils/numberFormat";

// Fixed viewBox, scaled purely by CSS (width: 100%) - this schematic isn't to scale in the first
// place, so there's no real detail to reveal by zooming, and no need to track the container's actual
// pixel width (that's only for ZoomableSectionPreview's pixel-based zoom/pan transforms).
const PREVIEW_SIZE: SectionPreviewSize = getPreviewSize();

type DeckSlopeViewCardProps = {
  className?: string;
  embedded?: boolean;
};

// One profile per side of the deck outline (4 for a rectangle, 6 for an L), so every corner's
// support height shows up. Corners are numbered like the top view's points (index + 1 over the same
// outline), and the terrain is the same plane as the 3D view and the support-height validation:
// through the deck's lowest vertex (where H applies), rising by slopeDegrees along
// slopeArrow - so each side's own slope follows from that direction.
const DeckSlopeViewCard: FC<DeckSlopeViewCardProps> = ({ className, embedded = false }) => {
  const i18n = useI18n();
  const measurementFormat = useMeasurementFormat();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const common = i18n.translation.common;
  const { control } = useFormContext<DeckDesignerConfigurationFormValues>();

  const shape = useWatch({ control, name: "shape", exact: true });
  const totalHeight = useWatch({ control, name: "totalHeight", exact: true });
  const base = useWatch({ control, name: "base", exact: true });
  const height = useWatch({ control, name: "height", exact: true });
  const secondaryBase = useWatch({ control, name: "secondaryBase", exact: true });
  const secondaryHeight = useWatch({ control, name: "secondaryHeight", exact: true });
  const lShapeCutoutCorner = useWatch({ control, name: "lShapeCutoutCorner", exact: true });
  const slopeDegrees = useWatch({ control, name: "slopeDegrees", exact: true });
  const slopeArrow = useWatch({ control, name: "slopeArrow", exact: true });
  const boardThickness = useWatch({ control, name: "boardThickness", exact: true });
  const substructure = useWatch({ control, name: "substructure", exact: true });
  const secondaryItem = useWatch({ control, name: "secondaryItem", exact: true });
  const connector = useWatch({ control, name: "connector", exact: true });

  const title = calculationTranslation.slopeView;

  const coordinates = useMemo(
    () => getFootprintCoordinates({ shape, base, height, secondaryBase, secondaryHeight, lShapeCutoutCorner }),
    [base, height, lShapeCutoutCorner, shape, secondaryBase, secondaryHeight],
  );
  const bounds = getCoordinateBounds(coordinates);

  const fixedElementsHeightMm =
    (substructure?.height ?? 0) + (secondaryItem?.thickness ?? 0) + (connector?.thickness ?? 0) + (boardThickness ?? 0);
  const hasData = totalHeight != null && bounds != null && coordinates.length >= 4;
  const totalHeightMm = hasData ? totalHeight * 10 : 0;
  const supportHeightBudgetMm = totalHeightMm - fixedElementsHeightMm;

  const riseMmAt = getTerrainRiseMmAt(coordinates, { slopeDegrees, slopeArrow });
  const dimensions = getLShapeDimensions(coordinates);
  const lengthFormatter = getNumberFormat(i18n.translation.languageCode, { maximumFractionDigits: 3 });
  const formatMm = (valueMm: number) => {
    const roundedMm = Math.round(valueMm);
    return measurementFormat.length(roundedMm, "mm", `${roundedMm} ${common.unitMm}`);
  };

  const sides = hasData
    ? coordinates.map((start, index) => {
        const endIndex = (index + 1) % coordinates.length;
        const end = coordinates[endIndex]!;
        const lengthMeters = getSegmentLength(start, end);
        const startRiseMm = riseMmAt(start);
        const endRiseMm = riseMmAt(end);
        const sideCode = getSideCode({ id: `side-${index}`, start, end, isPreview: false }, dimensions);

        return {
          key: `${index}-${endIndex}`,
          title: `${calculationTranslation.slopeViewSide(index + 1, endIndex + 1)}${sideCode ? ` (${sideCode})` : ""}`,
          runLabel: sideCode ?? "",
          lengthText: measurementFormat.length(
            lengthMeters,
            "m",
            `${lengthFormatter.format(lengthMeters)} ${common.unitM}`,
          ),
          slopeDegrees:
            lengthMeters > 0
              ? Math.atan(Math.abs(endRiseMm - startRiseMm) / (lengthMeters * METERS_TO_MILLIMETERS)) /
                DEGREES_TO_RADIANS
              : 0,
          nearHeightMm: supportHeightBudgetMm - startRiseMm,
          farHeightMm: supportHeightBudgetMm - endRiseMm,
          totalHeightSide:
            Math.abs(startRiseMm) < 0.5 ? ("near" as const) : Math.abs(endRiseMm) < 0.5 ? ("far" as const) : null,
        };
      })
    : [];

  const scale = PREVIEW_SIZE.width / 760;
  const notToScaleFontSize = 11 * scale;
  const legendFontSize = 11 * scale;
  const hatchTileSize = 8 * scale;
  const legendReservePx = 48 * scale;
  // Same height per profile as the original two-profile layout; the drawing grows with the number of
  // sides instead of squeezing them (the PDF gives this image a page of its own).
  const blockHeight = PREVIEW_SIZE.height / 2;
  const previewSize: SectionPreviewSize = {
    width: PREVIEW_SIZE.width,
    height: hasData ? blockHeight * sides.length + legendReservePx : PREVIEW_SIZE.height,
  };
  const usableHeight = Math.max(previewSize.height - legendReservePx, 1);

  const content = (
    <div className={styles.canvas}>
      <svg
        className={styles.drawing}
        viewBox={`0 0 ${previewSize.width} ${previewSize.height}`}
        role="img"
        aria-label={title}
      >
        <defs>
          <pattern
            id={GROUND_HATCH_ID}
            width={hatchTileSize}
            height={hatchTileSize}
            patternTransform="rotate(45)"
            patternUnits="userSpaceOnUse"
          >
            <rect width={hatchTileSize} height={hatchTileSize} fill={GROUND_FILL_COLOR} />
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={hatchTileSize}
              stroke={GROUND_HATCH_COLOR}
              strokeWidth={Math.max(hatchTileSize * 0.18, 1)}
            />
          </pattern>
        </defs>
        <rect x={0} y={0} width={previewSize.width} height={previewSize.height} fill="white" />
        {hasData ? (
          <>
            {sides.map((side, index) => (
              <SlopeSection
                key={side.key}
                blockIndex={index}
                blockHeight={blockHeight}
                title={side.title}
                lengthText={side.lengthText}
                runLabel={side.runLabel}
                slopeDegrees={side.slopeDegrees}
                nearHeightMm={side.nearHeightMm}
                farHeightMm={side.farHeightMm}
                totalHeightSide={side.totalHeightSide}
                totalHeightMm={totalHeightMm}
                previewSize={previewSize}
                unitDeg={common.unitDeg}
                formatMm={formatMm}
                unreachableLabel={calculationTranslation.slopeViewUnreachable}
              />
            ))}
            <text
              x={previewSize.width * 0.02}
              y={usableHeight + legendFontSize * 1.6}
              fontSize={legendFontSize}
              fill={TOTAL_HEIGHT_COLOR}
              fontWeight={700}
            >
              {calculationTranslation.slopeViewLegendTotalHeight}
            </text>
            <text
              x={previewSize.width * 0.02}
              y={usableHeight + legendFontSize * 3.1}
              fontSize={legendFontSize}
              fill={DIMENSION_COLOR}
              fontWeight={600}
            >
              {calculationTranslation.slopeViewLegendSupportHeight}
            </text>
            <text
              x={previewSize.width - previewSize.width * 0.02}
              y={usableHeight + legendFontSize * 3.1}
              textAnchor="end"
              fontSize={notToScaleFontSize}
              fontStyle="italic"
              fill={LABEL_COLOR}
            >
              {calculationTranslation.slopeViewNotToScale}
            </text>
          </>
        ) : (
          <text
            x={previewSize.width / 2}
            y={previewSize.height / 2}
            textAnchor="middle"
            fontSize={previewSize.width * (14 / 760)}
            fill={LABEL_COLOR}
          >
            {calculationTranslation.calculationDetailsEmpty}
          </text>
        )}
      </svg>
    </div>
  );

  return embedded ? (
    content
  ) : (
    <Card
      style={{ minHeight: 400 }}
      className={className}
      accentColor="var(--accent-base, var(--red))"
      shadow
      title={title}
    >
      {content}
    </Card>
  );
};

// Memoized: its props are stable, so it re-renders through its own watched fields and props only, not
// whenever its parent does.
export default memo(DeckSlopeViewCard);
